import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getDashboardStats, getDashboardChartData, getCheckinStatus, checkin, checkout, getServiceTypes } from '@/lib/api';
import { checkGeofence } from '@/lib/geofence';
import { Clock, CheckCircle, XCircle, AlertCircle, Users, TrendingUp, LogIn, LogOut, MapPin } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const CHART_COLORS = ['#D3735A', '#3F6352', '#4A788C', '#D4A017', '#B74134', '#2C5545'];

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [statsRes, chartRes] = await Promise.all([
          getDashboardStats(),
          getDashboardChartData()
        ]);
        setStats(statsRes.data);
        setChartData(chartRes.data);
      } catch (e) {
        console.error('Failed to load dashboard', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="animate-fade-in" data-testid="dashboard-loading">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white border border-[#E8E3DD] rounded-xl p-6 animate-pulse">
              <div className="h-4 bg-[#F2EFEB] rounded w-24 mb-3" />
              <div className="h-8 bg-[#F2EFEB] rounded w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const isAdmin = user?.role === 'admin' || user?.role === 'supervisor';

  return (
    <div className="animate-fade-in" data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="font-['Manrope'] text-3xl font-bold text-[#2D2B2A] tracking-tight">
          Welcome, {user?.name}
        </h1>
        <p className="text-[#5C5855] mt-1">
          {user?.role === 'volunteer' ? 'Thank you for being here. Check in below when you arrive.' :
           user?.role === 'supervisor' ? 'Review and approve volunteer hours' :
           'Manage the volunteer tracking system'}
        </p>
      </div>

      {/* Stats Grid */}
      <div className={`grid grid-cols-1 md:grid-cols-2 ${isAdmin ? 'lg:grid-cols-3' : 'lg:grid-cols-4'} gap-6 mb-8`}>
        <StatCard testId="stat-total-hours" icon={TrendingUp} label="Total Approved Hours" value={stats?.total_approved_hours || 0} color="#3F6352" />
        <StatCard testId="stat-pending" icon={AlertCircle} label="Pending" value={stats?.pending || 0} color="#D4A017" />
        <StatCard testId="stat-approved" icon={CheckCircle} label="Approved" value={stats?.approved || 0} color="#2C5545" />
        <StatCard testId="stat-rejected" icon={XCircle} label="Rejected" value={stats?.rejected || 0} color="#B74134" />
        {isAdmin && (
          <>
            <StatCard testId="stat-total-submissions" icon={Clock} label="Total Submissions" value={stats?.total_submissions || 0} color="#4A788C" />
            <StatCard testId="stat-total-volunteers" icon={Users} label="Total Volunteers" value={stats?.total_volunteers || 0} color="#D3735A" />
          </>
        )}
      </div>

      {/* Charts */}
      {chartData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Monthly Hours Bar Chart */}
          {chartData.monthly && chartData.monthly.length > 0 && (
            <div className="bg-white border border-[#E8E3DD] rounded-xl p-6" data-testid="monthly-chart">
              <h3 className="font-['Manrope'] text-lg font-bold text-[#2D2B2A] mb-4">Hours by Month</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData.monthly} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E3DD" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#5C5855' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#5C5855' }} />
                  <Tooltip
                    contentStyle={{ background: '#fff', border: '1px solid #E8E3DD', borderRadius: 8, fontSize: 13 }}
                    labelStyle={{ fontWeight: 600, color: '#2D2B2A' }}
                  />
                  <Bar dataKey="hours" fill="#3F6352" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Hours by Service Type Pie Chart */}
          {chartData.by_service && chartData.by_service.length > 0 && (
            <div className="bg-white border border-[#E8E3DD] rounded-xl p-6" data-testid="service-chart">
              <h3 className="font-['Manrope'] text-lg font-bold text-[#2D2B2A] mb-4">Hours by Service Type</h3>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={chartData.by_service}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={95}
                    paddingAngle={3}
                    dataKey="hours"
                    nameKey="name"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={{ stroke: '#8A847F' }}
                  >
                    {chartData.by_service.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: '#fff', border: '1px solid #E8E3DD', borderRadius: 8, fontSize: 13 }}
                    formatter={(value) => [`${value} hrs`, 'Hours']}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Daily Hours Trend */}
          {chartData.daily && chartData.daily.length > 0 && (
            <div className="bg-white border border-[#E8E3DD] rounded-xl p-6 lg:col-span-2" data-testid="daily-chart">
              <h3 className="font-['Manrope'] text-lg font-bold text-[#2D2B2A] mb-4">Daily Hours Trend</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={chartData.daily} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E3DD" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#5C5855' }} angle={-30} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 12, fill: '#5C5855' }} />
                  <Tooltip
                    contentStyle={{ background: '#fff', border: '1px solid #E8E3DD', borderRadius: 8, fontSize: 13 }}
                    labelStyle={{ fontWeight: 600, color: '#2D2B2A' }}
                  />
                  <Bar dataKey="hours" fill="#D3735A" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Check-in/out for volunteers, Quick Actions for admins */}
      {user?.role === 'volunteer' ? (
        <CheckInOutWidget />
      ) : (
        <div className="bg-white border border-[#E8E3DD] rounded-xl p-6">
          <h2 className="font-['Manrope'] text-lg font-bold text-[#2D2B2A] mb-4">Quick Actions</h2>
          <div className="flex flex-wrap gap-3">
            {isAdmin && (
              <a href="/approve-hours" className="inline-flex items-center gap-2 bg-[#3F6352] hover:bg-[#2C4A3C] text-white rounded-lg px-5 py-2.5 text-sm font-semibold transition-colors" data-testid="quick-approve-hours">
                <CheckCircle className="h-4 w-4" /> Approve Hours
              </a>
            )}
            <a href="/submit-hours" className="inline-flex items-center gap-2 bg-[#D3735A] hover:bg-[#BD6047] text-white rounded-lg px-5 py-2.5 text-sm font-semibold transition-colors" data-testid="quick-submit-hours">
              <Clock className="h-4 w-4" /> Submit Hours
            </a>
            <a href="/reports" className="inline-flex items-center gap-2 bg-[#F2EFEB] hover:bg-[#E8E3DD] text-[#2D2B2A] border border-[#D1C9C0] rounded-lg px-5 py-2.5 text-sm font-semibold transition-colors" data-testid="quick-reports">
              <TrendingUp className="h-4 w-4" /> View Reports
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, testId }) {
  return (
    <div className="bg-white border border-[#E8E3DD] shadow-[0_2px_12px_-4px_rgba(45,43,42,0.06)] rounded-xl p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_8px_24px_-8px_rgba(45,43,42,0.12)]" data-testid={testId}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon className="h-5 w-5" style={{ color }} />
        </div>
        <span className="text-xs tracking-[0.05em] uppercase font-bold text-[#5C5855]">{label}</span>
      </div>
      <p className="text-3xl font-bold text-[#2D2B2A] font-['Manrope']">{value}</p>
    </div>
  );
}

function CheckInOutWidget() {
  const [status, setStatus] = useState(null);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [statusRes, typesRes] = await Promise.all([getCheckinStatus(), getServiceTypes()]);
        setStatus(statusRes.data.active_checkin);
        setServiceTypes(typesRes.data.service_types || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleCheckin = async () => {
    if (!selectedService) { toast.error('Please select a service type'); return; }
    setProcessing(true);
    try {
      const geo = await checkGeofence();
      if (!geo.allowed) {
        toast.error(geo.message);
        setProcessing(false);
        return;
      }
      await checkin({ service_type: selectedService });
      toast.success('Checked in! Your hours are being tracked.');
      const { data } = await getCheckinStatus();
      setStatus(data.active_checkin);
    } catch (e) { toast.error(e.response?.data?.detail || 'Check-in failed'); }
    finally { setProcessing(false); }
  };

  const handleCheckout = async () => {
    setProcessing(true);
    try {
      const geo = await checkGeofence();
      if (!geo.allowed) {
        toast.error(geo.message);
        setProcessing(false);
        return;
      }
      const { data } = await checkout();
      toast.success(`Checked out! ${data.hours} hours logged for approval.`);
      setStatus(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Check-out failed'); }
    finally { setProcessing(false); }
  };

  if (loading) return null;

  return (
    <div className="bg-white border border-[#E8E3DD] rounded-xl p-6" data-testid="checkin-widget">
      {status ? (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-3 h-3 rounded-full bg-[#2C5545] animate-pulse" />
            <h2 className="font-['Manrope'] text-lg font-bold text-[#2D2B2A]">Currently Checked In</h2>
          </div>
          <div className="bg-[#FAF8F5] rounded-lg p-4 mb-4">
            <p className="text-sm text-[#5C5855]">Service: <strong className="text-[#2D2B2A]">{status.service_type}</strong></p>
            <p className="text-sm text-[#5C5855] mt-1">Time: <strong className="text-[#2D2B2A]">{status.elapsed_hours} hours</strong> so far</p>
          </div>
          <Button onClick={handleCheckout} disabled={processing} data-testid="checkout-btn"
            className="bg-[#D3735A] hover:bg-[#BD6047] text-white rounded-lg px-6 py-3 font-semibold w-full h-12">
            {processing ? 'Checking out...' : <><LogOut className="h-4 w-4 mr-2" /> Check Out</>}
          </Button>
        </div>
      ) : (
        <div>
          <h2 className="font-['Manrope'] text-lg font-bold text-[#2D2B2A] mb-4">Start Volunteering</h2>
          <div className="mb-4">
            <Select value={selectedService} onValueChange={setSelectedService}>
              <SelectTrigger data-testid="checkin-service-select" className="h-12 border-[#D1C9C0] bg-white rounded-lg">
                <SelectValue placeholder="What are you helping with today?" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {serviceTypes.map((st) => (
                  <SelectItem key={st} value={st}>{st}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleCheckin} disabled={processing} data-testid="checkin-btn"
            className="bg-[#3F6352] hover:bg-[#2C4A3C] text-white rounded-lg px-6 py-3 font-semibold w-full h-12">
            {processing ? 'Checking in...' : <><LogIn className="h-4 w-4 mr-2" /> Check In</>}
          </Button>
        </div>
      )}
    </div>
  );
}
